import { useState, type FormEvent } from 'react';
import { ApiError } from '@rydres/api-client';
import { BRAND } from '@rydres/shared';
import { Button, Input, Logo, toE164 } from '@rydres/ui';
import { useSession } from '../lib/session.js';

export function SignIn() {
  const { requestCode, signIn } = useSession();
  const [phoneInput, setPhoneInput] = useState('');
  const [phone, setPhone] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const p = toE164(phoneInput);
    if (!p) return setError('Enter a valid mobile number');
    setBusy(true);
    setError(null);
    try {
      await requestCode(p);
      setPhone(p);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send the code');
    } finally {
      setBusy(false);
    }
  };
  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!phone) return;
    setBusy(true);
    setError(null);
    try {
      await signIn(phone, code.trim());
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401 ? 'That code is not right' : 'Could not sign in',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <form onSubmit={phone ? verify : send} className="w-full max-w-sm space-y-4">
        <div>
          <h1><Logo size={48} className="text-3xl" /></h1>
          <p className="mt-2 text-sm text-muted">{BRAND.tagline}</p>
        </div>
        {!phone ? (
          <>
            <Input
              autoFocus
              inputMode="tel"
              autoComplete="tel"
              placeholder="Your mobile number"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
            />
            {error && <p className="text-sm text-danger">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full">
              Text me a code
            </Button>
            <p className="text-xs text-muted">
              For licensed TCP drivers. Signing in creates your account if you are new.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-muted">We texted a code to {phone}.</p>
            <Input
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="text-center text-2xl tracking-[0.4em]"
            />
            {error && <p className="text-sm text-danger">{error}</p>}
            <Button type="submit" disabled={busy || code.length < 4} className="w-full">
              Sign in
            </Button>
            <button type="button" className="w-full text-sm text-muted" onClick={() => setPhone(null)}>
              Use a different number
            </button>
          </>
        )}
      </form>
    </div>
  );
}
